/* eslint-disable @next/next/no-img-element */
import React from "react";

import Image, { StaticImageData } from "next/image";

import classNames from "classnames/bind";
import ShortlistButton from "features/favourites/components/ShortlistButton";
import useHasMounted from "hooks/useHasMounted/useHasMounted";
import useImageExists from "hooks/useImageExists/useImageExists";
import useMediaQuery from "hooks/useMediaQuery/useMediaQuery";
import { DEFAULT_COMINGSOON_IMAGE_URL, DEFAULT_IMAGE_URL } from "lib/constants";
import { ResolvedVehicleCardSettings } from "resolvers/data/vehicleCardSettingsResolver";
import { VehicleService } from "services/vehicle/vehicle";
import { vehicleImageOverlayService } from "services/vehicle/vehicleImageOverlayService";
import slugify from "slugify";
import { Keyboard, Navigation, Thumbs } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import styles from "../../VehicleOverview.module.scss";

const cx = classNames.bind(styles);

export type VehicleImageProps = {
    handleImageViewerImageOpen: (activeIndex: number) => void;
    currentSelectedImageIndex: number;
    setCurrentSelectedImageIndex: React.Dispatch<React.SetStateAction<number>>;
    googlePlaceId?: string;
    isSaved?: boolean;
    vehicle: VehicleService.Vehicle;
    thumbsSwiperRef: React.MutableRefObject<any>;
    vehicleCardSettings: ResolvedVehicleCardSettings;
};

const VehicleImage = React.forwardRef<HTMLDivElement, VehicleImageProps>((props, ref) => {
    const {
        vehicle,
        currentSelectedImageIndex,
        handleImageViewerImageOpen,
        setCurrentSelectedImageIndex,
        googlePlaceId,
        isSaved,
        thumbsSwiperRef,
        vehicleCardSettings,
    } = props;
    const { getImageOverlayForVehicle } = vehicleImageOverlayService();

    const { isMobile } = useMediaQuery();

    const hasImagery = vehicle.Imagery.MarketingImages.length > 0;
    const currentImage = vehicle.Imagery.MarketingImages[currentSelectedImageIndex]?.Uri;
    const currentDescription =
        vehicle.Imagery.MarketingImages[currentSelectedImageIndex]?.Description;

    const hasMounted = useHasMounted();
    const campaignImageOverlay = getImageOverlayForVehicle(
        vehicle,
        vehicleCardSettings.marketingCampaigns
    );

    let imageSrc: string | StaticImageData = DEFAULT_IMAGE_URL;

    if (vehicle.ComingSoonDetails?.IsComingSoon) {
        const manufacturerImageUrl = `https://assets.motorpoint.co.uk/vehicle-advert/coming-soon/${slugify(
            vehicle.Taxonomy.Manufacturer.toLowerCase()
        )}/1024.jpg`;
        const imageExists = useImageExists(manufacturerImageUrl);
        imageSrc = !imageExists ? DEFAULT_COMINGSOON_IMAGE_URL : manufacturerImageUrl;
    } else if (currentImage) imageSrc = currentImage;

    if (isMobile && vehicle.Imagery.MarketingImages.length && hasMounted) {
        return (
            <div ref={ref} id="vehicle-image-container" className={cx("vehicleOverviewFigure")}>
                <Swiper
                    mousewheel={true}
                    keyboard={true}
                    // freeMode={true}
                    watchSlidesProgress={true}
                    modules={[Navigation, Thumbs, Keyboard]}
                    spaceBetween={10}
                    slidesOffsetBefore={0}
                    loop
                    slidesPerView={1}
                    navigation={{
                        nextEl: "#vehicleImageNextButton",
                        prevEl: "#vehicleImagePrevButton",
                    }}
                    thumbs={{
                        swiper: thumbsSwiperRef.current ? (thumbsSwiperRef.current as any) : null,
                    }}
                >
                    {vehicle.Imagery.MarketingImages.map((image, index) => {
                        return (
                            <SwiperSlide
                                key={`vehicle-carousel-${image.Angle}`}
                                onClick={() => handleImageViewerImageOpen(index)}
                            >
                                <img
                                    src={image.Uri}
                                    alt={image.Description}
                                    width={727}
                                    height={485}
                                    style={{ objectFit: "contain", height: "auto" }}
                                    loading={index <= 5 ? "eager" : "lazy"}
                                    fetchPriority={index == 0 ? "high" : "low"}
                                />
                            </SwiperSlide>
                        );
                    })}
                </Swiper>
                <ShortlistButton
                    vehicleId={vehicle.StockBookNumber}
                    isSaved={!!isSaved}
                    isConfirmationNeeded={false}
                    large
                    className={cx("shortlistButton")}
                    tagManagerEventData={{
                        vehicle,
                        googlePlaceId,
                    }}
                />
                {!!campaignImageOverlay && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                        className={cx("campaignImageOverlay")}
                        src={campaignImageOverlay}
                        alt="Promotional campaign image"
                    />
                )}
            </div>
        );
    }
    return (
        <div ref={ref} id="vehicle-image-container">
            <figure
                className={cx("vehicleOverviewFigure", !hasImagery && "no-cursor")}
                onClick={() => handleImageViewerImageOpen(currentSelectedImageIndex)}
            >
                <Image
                    src={imageSrc}
                    alt={currentDescription ?? "Coming Soon"}
                    width={727}
                    height={485}
                    style={{ objectFit: "cover", height: "auto" }}
                    fetchPriority={"high"}
                    loading="eager"
                    className={cx("defaultImage")}
                />
                {!!campaignImageOverlay && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                        className={cx("campaignImageOverlay")}
                        src={campaignImageOverlay}
                        alt="Promotional campaign image"
                    />
                )}
            </figure>
            <ShortlistButton
                vehicleId={vehicle.StockBookNumber}
                isSaved={!!isSaved}
                isConfirmationNeeded={false}
                large
                className={cx("shortlistButton")}
                tagManagerEventData={{
                    vehicle,
                    googlePlaceId,
                }}
            />
        </div>
    );
});

VehicleImage.displayName = "VehicleImage";

export default VehicleImage;
